import * as React from 'react';
import { memo } from 'react';
import { Link } from 'react-router-dom';

import { DataTable, DataTableContent, DataTableHead, DataTableRow, DataTableHeadCell, DataTableBody, DataTableCell } from '@rmwc/data-table';
import '@rmwc/data-table/styles';
import { Button } from '@rmwc/button';
import '@rmwc/button/styles';

import { FlexibleChannelInfo, TrackerData, TrackerSetupData } from '../types';
import { getPoleState, getTrackerState, getJointState } from '../state_calc';
import { StateIndicator } from './state_indicator';
import { InteractiveRow } from './site_row_wrapper';
import { UiState } from './ui_state';
import { FeatherIcon } from './feather_icon';

const getTrackerSetupState = (setup: TrackerSetupData | undefined) => {
  if(setup == null || setup.address == null) {
    return 'error';
  } 

  if(setup.setup == null || setup.current == null) {
    return 'warning';
  }

  return 'okay';
}

interface TrackerSetupListRowProps {
  tracker: TrackerData;
  setup: TrackerSetupData | undefined;
}

const TrackerSetupListRow: React.FC<TrackerSetupListRowProps> = ({ tracker, setup }) => {
  const state = getTrackerSetupState(setup);
  const poles = React.useMemo(() => getPoleState(tracker), [tracker]);
  const joints = React.useMemo(() => getJointState(tracker), [tracker]);

  const invertString = (data: number | undefined) => {
    if(data == null) {
      return '-';
    }

    if(data) {
      return 'inverted';
    } else {
      return 'normal';
    }
  };

  const channelString = (data: FlexibleChannelInfo | null | undefined) => {
    if(data == null) {
      return '-';
    }

    if(data.length === 4) {
      return `${data[0]} (${data[3]})`;
    } else {
      return data[0];
    }
  };

  return <>
    <DataTableCell className="state_cell"><StateIndicator state={state} /></DataTableCell>
    <DataTableCell>{tracker.id}</DataTableCell>
    <DataTableCell>{tracker.name ?? '-'}</DataTableCell>
    <DataTableCell isNumeric>{invertString(setup?.setup?.invert_motor_direction)}</DataTableCell>
    <DataTableCell isNumeric>{invertString(setup?.setup?.invert_target_angle)}</DataTableCell>
    <DataTableCell isNumeric>{setup?.setup?.offset_angle ?? '-'}</DataTableCell>
    <DataTableCell isNumeric>{setup?.setup?.safe_position ?? '-'}</DataTableCell>
    <DataTableCell isNumeric>{setup?.setup?.min_angle ?? '-'}</DataTableCell>
    <DataTableCell isNumeric>{setup?.setup?.max_angle ?? '-'}</DataTableCell>
    <DataTableCell isNumeric>{channelString(setup?.current)}</DataTableCell>
    <DataTableCell isNumeric>{setup?.address ?? '-'}</DataTableCell>
  </>;
}

interface TrackerRowProps {
  siteId: string | undefined;
  tracker: TrackerData;
  setup: TrackerSetupData | undefined;
  uiState: UiState;
}

const TrackerRow: React.FC<TrackerRowProps> = memo(({ siteId, tracker, setup, uiState }) => {
  return <InteractiveRow id={tracker.id} uiState={uiState}>
    <TrackerSetupListRow tracker={tracker} setup={setup} />
  </InteractiveRow>;
});

const TrackerSetupListHeader: React.FC = () => {
  return <>
    <DataTableHeadCell></DataTableHeadCell>
    <DataTableHeadCell className="table_id">Internal Name</DataTableHeadCell>
    <DataTableHeadCell className="table_tracker">Customer Name</DataTableHeadCell>
    <DataTableHeadCell className="table_motor_inv numeric_head">Motor Invert</DataTableHeadCell>
    <DataTableHeadCell className="table_angle_inv numeric_head">Angle Invert</DataTableHeadCell>
    <DataTableHeadCell className="table_calibration numeric_head">Calibration</DataTableHeadCell>
    <DataTableHeadCell className="table_safe numeric_head">Safe Position</DataTableHeadCell>
    <DataTableHeadCell className="table_min_angle numeric_head">Min Angle</DataTableHeadCell>
    <DataTableHeadCell className="table_max_angle numeric_head">Max Angle</DataTableHeadCell>
    <DataTableHeadCell className="table_channel numeric_head">RF Channel</DataTableHeadCell>
    <DataTableHeadCell className="table_controller numeric_head">Track.bloxx Address</DataTableHeadCell>
  </>;
}

export interface TrackerSetupListProps {
  siteId: string | undefined;
  trackers: TrackerData[] | undefined;
  setup: Map<string,TrackerSetupData> | undefined;
  empty?: boolean;
  uiState: UiState;
}

export const TrackerSetupList: React.FC<TrackerSetupListProps> = memo(({ siteId, trackers, setup, empty, uiState }) => {
  let body: React.ReactNode;

  if(trackers == null || setup == null) {
    body = <DataTableRow>
      <DataTableCell colSpan={99} className="message_row">
        Loading ...
      </DataTableCell>
    </DataTableRow>;
  } else if(empty) {
    body = <DataTableRow>
      <DataTableCell colSpan={99} className="message_row">
        There are no trackers in this project, yet.
      </DataTableCell>
    </DataTableRow>;
  } else if(trackers.length === 0) {
    body = <DataTableRow>
      <DataTableCell colSpan={99} className="message_row">
        No trackers matched the criteria.
      </DataTableCell>
    </DataTableRow>;
  } else {
    body = trackers.map((data) => {
      return <TrackerRow key={data.id} siteId={siteId} tracker={data} setup={setup.get(data.id)} uiState={uiState} />;
    });
  }

  return <>
    <DataTable stickyRows={1}>
      <DataTableContent>
        <DataTableHead>
          <DataTableRow>
            <TrackerSetupListHeader />
          </DataTableRow>
        </DataTableHead>
        <DataTableBody>
          {body}
        </DataTableBody>
      </DataTableContent>
    </DataTable>
  </>;
});
